import { Injectable, OnDestroy } from '@angular/core'
import { ToastrService } from 'ngx-toastr'
import { environment } from '../../../environments/environment'

// Detects when a newer build has been deployed while the browser was left open
// (or when an old cached index.html was loaded) by comparing the app-version meta tag
// in a freshly fetched index.html against the version compiled into this bundle.
@Injectable({ providedIn: 'root' })
export class VersionCheckService implements OnDestroy {
  private readonly checkIntervalMs = 5 * 60 * 1000
  private timer: ReturnType<typeof setInterval> | null = null
  private notified = false

  constructor(private toastrService: ToastrService) {}

  public start(): void {
    if (!environment.production || this.timer) {
      return
    }
    this.check()
    this.timer = setInterval(() => this.check(), this.checkIntervalMs)
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  }

  ngOnDestroy(): void {
    if (this.timer) {
      clearInterval(this.timer)
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
  }

  private onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      this.check()
    }
  }

  private async check(): Promise<void> {
    if (this.notified) {
      return
    }
    try {
      const response = await fetch(`${document.baseURI}index.html?t=${Date.now()}`, {
        cache: 'no-store',
      })
      if (!response.ok) {
        return
      }
      const html = await response.text()
      const latest = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('meta[name="app-version"]')
        ?.getAttribute('content')

      if (latest && latest !== environment.appVersion) {
        this.notifyUpdate(latest)
      }
    } catch {
      // Network blips or offline - try again on the next interval
    }
  }

  private notifyUpdate(latest: string): void {
    this.notified = true

    // Nothing to lose on the login screen, so reload straight away.
    // Only once per version, so a mismatched deploy can't cause a reload loop.
    if (this.isLoginPage() && this.readReloadedFor() !== latest) {
      this.writeReloadedFor(latest)
      window.location.reload()
      return
    }

    this.toastrService
      .info(
        `A new version (${latest}) is available. Click here to reload.`,
        'Update available',
        { disableTimeOut: true, tapToDismiss: true, closeButton: true, positionClass: 'toast-top-center' },
      )
      .onTap.subscribe(() => window.location.reload())
  }

  private isLoginPage(): boolean {
    const path = window.location.pathname.replace(/\/+$/, '')
    return path === '' || path === '/auth' || path === '/auth/auth'
  }

  private readReloadedFor(): string | null {
    try {
      return sessionStorage.getItem('versionReloadedFor')
    } catch {
      return null
    }
  }

  private writeReloadedFor(version: string): void {
    try {
      sessionStorage.setItem('versionReloadedFor', version)
    } catch {
      // Storage blocked - the toast path still covers it on the next check
    }
  }
}
